import { Infer, t } from 'typegate';
import { participantSchema } from '../participant';

export const participantMedicalInterviewStatusSchema = t.union(
  t.literal('NEW'),
  t.literal('READY'),
  t.literal('STARTED'),
  t.literal('COMPLETED'),
  t.literal('NIGO'),
  t.literal('SKIPPED'),
);

export type ParticipantMedicalInterviewStatus = Infer<typeof participantMedicalInterviewStatusSchema>;

const participantMedicalInterviewRequestDataSchema = t.record(t.string, t.any);
const participantMedicalInterviewDataSchema = t.object(
  t.property('skipped', t.boolean),
  t.property('ready', t.boolean),
  t.property('data', participantMedicalInterviewRequestDataSchema),
);

export type ParticipantMedicalInterviewData = Infer<typeof participantMedicalInterviewDataSchema>;

export type {
  ParticipantMedicalInterviewRequestData,
  ProductMapping,
  ReplacementInsurance,
} from './participantMedicalInterviewData';

const participantMedicalInterviewResponseDataSchema = t.object(
  t.property('deContext', t.record(t.string, t.unknown)),
  t.property('interviewDetails', t.string),
  t.property('rulesResults', t.string),
);

export const participantMedicalInterviewSchema = t
  .object(
    t.property('id', t.string),
    t.property('participantId', t.string),
    t.property('status', participantMedicalInterviewStatusSchema),
    t.optionalProperty('requestData', participantMedicalInterviewRequestDataSchema),
    t.optionalProperty('requestXML', t.string),
    t.optionalProperty('response', participantMedicalInterviewResponseDataSchema),
    t.property('createdAt', t.string),
    t.property('updatedAt', t.string),
  )
  .setTypeName('ParticipantMedicalInterview');

export type ParticipantMedicalInterview = Infer<typeof participantMedicalInterviewSchema>;

const medicalInterviewWithParticipantSchema = t
  .intersection(participantMedicalInterviewSchema, t.object(t.property('participant', participantSchema)))
  .setTypeName('ParticipantMedicalInterviewWithParticipant');

export type ParticipantMedicalInterviewWithParticipant = Infer<typeof medicalInterviewWithParticipantSchema>;

export const updateParticipantMedicalInterviewPayloadSchema = t
  .intersection(
    t.required(t.pick(participantMedicalInterviewSchema, ['participantId'])),
    t.partial(t.pick(participantMedicalInterviewSchema, ['id', 'status', 'requestData', 'requestXML', 'response'])),
  )
  .setTypeName('UpdateParticipantMedicalInterviewPayload');

export type UpdateParticipantMedicalInterviewPayload = Infer<typeof updateParticipantMedicalInterviewPayloadSchema>;

export const storeParticipantMedicalInterviewResponsePayloadSchema = t
  .pick(updateParticipantMedicalInterviewPayloadSchema, ['participantId', 'response'])
  .setTypeName('StoreParticipantMedicalInterviewResponsePayload');

export type StoreParticipantMedicalInterviewResponsePayload = Infer<
  typeof storeParticipantMedicalInterviewResponsePayloadSchema
>;

const createParticipantMedicalInterviewPayloadSchema = t
  .object(
    t.property('participantId', t.string),
    t.optionalProperty('status', participantMedicalInterviewStatusSchema),
    t.property('requestData', participantMedicalInterviewRequestDataSchema),
    t.optionalProperty('requestXML', t.string),
    t.optionalProperty('response', t.record(t.string, t.any)),
  )
  .setTypeName('CreateParticipantMedicalInterviewPayload');

export type CreateParticipantMedicalInterviewPayload = Infer<typeof createParticipantMedicalInterviewPayloadSchema>;
